import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Field, CreateFieldDto, UpdateFieldDto } from '../models/field.model';
import { OrganizationContextService } from './organization-context.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * Field Option for dropdowns
 */
export interface FieldOption {
  id: string;
  name: string;
  acreage: number;
  cropType: string;
  customerId: string | null;
  customerName: string | null;
  latitude: number | null;
  longitude: number | null;
  hasBoundary: boolean;
  agremoFieldId: string | null;
}

/**
 * Field Service
 *
 * Handles field management with real API integration.
 * Uses signal-based state management for reactive UI updates.
 *
 * Reference: /agent-os/specs/2026-01-07-customers-fields-backend/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class FieldService {
  private http = inject(HttpClient);
  private organizationContext = inject(OrganizationContextService);
  private apiUrl = environment.apiUrl;

  // Signal-based state management for fields
  private fieldsSignal = signal<Field[]>([]);
  private fieldOptionsSignal = signal<FieldOption[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly fields = computed(() => this.fieldsSignal());
  readonly fieldOptions = computed(() => this.fieldOptionsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    // React to organization context changes — load fields when org becomes available
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        this.loadFields();
        this.loadFieldOptions();
      }
    });
  }

  /**
   * Load fields from API
   */
  async loadFields(
    customerId?: string,
    cropType?: string,
    search?: string,
    page: number = 1,
    limit: number = 100
  ): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = { page, limit };
      if (customerId) params.customerId = customerId;
      if (cropType) params.cropType = cropType;
      if (search) params.search = search;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<Field[]>>(`${this.apiUrl}/fields`, { params })
      );

      if (response.success && response.data) {
        this.fieldsSignal.set(response.data);
      } else {
        throw new Error(response.error || 'Failed to load fields');
      }
    } catch (error: any) {
      console.error('Error loading fields:', error);
      this.errorSignal.set(error.message || 'Failed to load fields');
      // Keep existing data on error
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Load field options from API (for dropdowns)
   */
  private async loadFieldOptions(): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<FieldOption[]>>(`${this.apiUrl}/fields/options`)
      );

      if (response.success && response.data) {
        this.fieldOptionsSignal.set(response.data);
      }
    } catch (error: any) {
      console.error('Error loading field options:', error);
    }
  }

  /**
   * Get field options (synchronous, from cached signal)
   * Returns field options for use in dropdowns
   */
  getFieldOptions(): Field[] {
    // Convert FieldOption[] to Field[] for backward compatibility
    return this.fieldOptionsSignal().map(opt => ({
      id: opt.id,
      organizationId: '', // Not included in options
      customerId: opt.customerId,
      name: opt.name,
      acreage: opt.acreage,
      cropType: opt.cropType,
      latitude: opt.latitude,
      longitude: opt.longitude,
      address: undefined,
      city: undefined,
      state: undefined,
      notes: undefined,
      createdAt: '',
      updatedAt: ''
    } as Field));
  }

  /**
   * Refresh field options from API
   */
  async refreshFieldOptions(): Promise<void> {
    await this.loadFieldOptions();
  }

  /**
   * Get all fields (from signal)
   */
  getFields(): Field[] {
    return this.fieldsSignal();
  }

  /**
   * Get field by ID (from signal)
   */
  getFieldById(id: string): Field | undefined {
    return this.fieldsSignal().find(f => f.id === id);
  }

  /**
   * Get fields by customer ID (from signal)
   */
  getFieldsByCustomerId(customerId: string): Field[] {
    return this.fieldsSignal().filter(f => f.customerId === customerId);
  }

  /**
   * Get field by ID from API
   */
  async fetchFieldById(id: string): Promise<Field | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<Field>>(`${this.apiUrl}/fields/${id}`)
      );

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching field:', error);
      this.errorSignal.set(error.message || 'Failed to fetch field');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Create a new field
   */
  async createField(dto: CreateFieldDto): Promise<Field | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<Field>>(`${this.apiUrl}/fields`, dto)
      );

      if (response.success && response.data) {
        const newField = response.data;
        // Add to fields signal
        this.fieldsSignal.update(fields => [...fields, newField]);
        // Also add to field options signal for dropdowns
        this.fieldOptionsSignal.update(options => [...options, {
          id: newField.id,
          name: newField.name,
          acreage: newField.acreage,
          cropType: newField.cropType,
          customerId: newField.customerId || null,
          customerName: null,
          latitude: newField.latitude ?? null,
          longitude: newField.longitude ?? null,
          hasBoundary: !!newField.boundaryGeojson,
          agremoFieldId: newField.agremoFieldId || null
        }]);
        return newField;
      }

      throw new Error(response.error || 'Failed to create field');
    } catch (error: any) {
      console.error('Error creating field:', error);
      this.errorSignal.set(error.message || 'Failed to create field');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update an existing field
   */
  async updateField(id: string, dto: UpdateFieldDto): Promise<Field | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<Field>>(`${this.apiUrl}/fields/${id}`, dto)
      );

      if (response.success && response.data) {
        // Update local state
        this.fieldsSignal.update(fields => {
          const index = fields.findIndex(f => f.id === id);
          if (index !== -1) {
            const newFields = [...fields];
            newFields[index] = response.data!;
            return newFields;
          }
          return fields;
        });

        return response.data;
      }

      throw new Error(response.error || 'Failed to update field');
    } catch (error: any) {
      console.error('Error updating field:', error);
      this.errorSignal.set(error.message || 'Failed to update field');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Delete (soft-delete) a field
   */
  async deleteField(id: string): Promise<boolean> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(`${this.apiUrl}/fields/${id}`)
      );

      if (response.success) {
        // Remove from local state
        this.fieldsSignal.update(fields => fields.filter(f => f.id !== id));
        return true;
      }

      throw new Error(response.error || 'Failed to delete field');
    } catch (error: any) {
      console.error('Error deleting field:', error);
      this.errorSignal.set(error.message || 'Failed to delete field');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Sync a field to Agremo
   * Creates the field in Agremo and stores the returned agremoFieldId.
   * Requires the field to have a boundaryGeojson defined.
   */
  async syncToAgremo(fieldId: string): Promise<Field> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<Field>>(`${this.apiUrl}/fields/${fieldId}/sync-agremo`, {})
    );

    if (response.success && response.data) {
      // Update local fields signal with the new agremoFieldId
      this.fieldsSignal.update(fields => {
        const index = fields.findIndex(f => f.id === fieldId);
        if (index !== -1) {
          const newFields = [...fields];
          newFields[index] = { ...newFields[index], ...response.data! };
          return newFields;
        }
        return fields;
      });

      return response.data;
    }

    throw new Error(response.error || 'Failed to sync field to Agremo');
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
